/**
 * WebCodecs audio extraction strategy
 * Uses native browser APIs for high-performance audio extraction from MP4/MOV
 */

import type {
  AudioExtractionStrategy,
  ProgressCallback,
  ExtractionStrategy,
  DemuxedAudioTrack,
  PCMAudioData,
} from '../types';
import { isWebCodecsSupported, isOpusEncoderSupported } from '../capabilities';
import { demuxMP4Audio, isMP4Format } from '../demuxer/mp4-demuxer';
import { WebMOpusMuxer } from '../encoder/webm-opus-muxer';

const OUTPUT_SAMPLE_RATE = 48000;
const OUTPUT_CHANNELS = 1;
const OUTPUT_BITRATE = 128000;

export class WebCodecsStrategy implements AudioExtractionStrategy {
  readonly name: ExtractionStrategy = 'webcodecs';

  private opusSupported: boolean | null = null;

  async checkSupport(): Promise<boolean> {
    if (!isWebCodecsSupported()) {
      return false;
    }

    if (this.opusSupported === null) {
      this.opusSupported = await isOpusEncoderSupported();
    }

    return this.opusSupported;
  }

  canHandle(file: File): boolean {
    // Only handle MP4/MOV (has mp4box.js support)
    return isMP4Format(file);
  }

  async extract(file: File, onProgress?: ProgressCallback): Promise<File> {
    // Phase 1: Demux audio track (0-30%)
    const audioTrack = await demuxMP4Audio(file, {
      onProgress: (p) => {
        onProgress?.({
          stage: 'demuxing',
          progress: p * 0.3,
          strategy: 'webcodecs',
        });
      },
    });

    // Phase 2: Decode audio to PCM (30-60%)
    const pcmData = await this.decodeAudio(audioTrack, (p) => {
      onProgress?.({
        stage: 'decoding',
        progress: 0.3 + p * 0.3,
        strategy: 'webcodecs',
      });
    });

    // Phase 3: Encode to Opus and mux to WebM (60-100%)
    const webmBlob = await this.encodeToWebM(pcmData, (p) => {
      onProgress?.({
        stage: 'encoding',
        progress: 0.6 + p * 0.4,
        strategy: 'webcodecs',
      });
    });

    return new File([webmBlob], this.getOutputFileName(file.name), {
      type: 'audio/webm',
      lastModified: Date.now(),
    });
  }

  /**
   * Decode audio samples to PCM using WebCodecs AudioDecoder
   */
  private async decodeAudio(
    track: DemuxedAudioTrack,
    onProgress: (progress: number) => void
  ): Promise<PCMAudioData> {
    return new Promise((resolve, reject) => {
      const decodedFrames: Float32Array[] = [];
      let processedSamples = 0;
      const totalSamples = track.samples.length;

      const decoder = new AudioDecoder({
        output: (audioData: AudioData) => {
          // Extract PCM data from AudioData
          const channels = audioData.numberOfChannels;
          const frames = audioData.numberOfFrames;

          // Mix to mono if needed
          if (channels === 1) {
            const buffer = new Float32Array(frames);
            audioData.copyTo(buffer, { planeIndex: 0, format: 'f32-planar' });
            decodedFrames.push(buffer);
          } else {
            // Mix multiple channels to mono
            const mono = new Float32Array(frames);
            for (let ch = 0; ch < channels; ch++) {
              const buffer = new Float32Array(frames);
              audioData.copyTo(buffer, { planeIndex: ch, format: 'f32-planar' });
              for (let i = 0; i < frames; i++) {
                mono[i] += buffer[i] / channels;
              }
            }
            decodedFrames.push(mono);
          }

          audioData.close();
        },
        error: (error: DOMException) => {
          reject(new Error(`Audio decoding failed: ${error.message}`));
        },
      });

      // Configure decoder
      const config: AudioDecoderConfig = {
        codec: track.codec,
        sampleRate: track.sampleRate,
        numberOfChannels: track.channelCount,
      };

      if (track.description) {
        config.description = track.description;
      }

      decoder.configure(config);

      // Decode all samples
      for (const sample of track.samples) {
        const chunk = new EncodedAudioChunk({
          type: sample.isKeyFrame ? 'key' : 'delta',
          timestamp: sample.timestamp,
          duration: sample.duration,
          data: sample.data,
        });

        decoder.decode(chunk);
        processedSamples++;
        onProgress(processedSamples / totalSamples);
      }

      // Flush decoder
      decoder.flush().then(() => {
        decoder.close();
        resolve({
          samples: decodedFrames,
          sampleRate: track.sampleRate,
          channelCount: 1, // Already mixed to mono
        });
      }).catch(reject);
    });
  }

  /**
   * Encode PCM to Opus and mux to WebM
   */
  private async encodeToWebM(
    pcmData: PCMAudioData,
    onProgress: (progress: number) => void
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const muxer = new WebMOpusMuxer({
        sampleRate: OUTPUT_SAMPLE_RATE,
        channelCount: OUTPUT_CHANNELS,
      });

      let processedFrames = 0;
      const totalFrames = pcmData.samples.length;

      const encoder = new AudioEncoder({
        output: (chunk: EncodedAudioChunk, _metadata?: EncodedAudioChunkMetadata) => {
          // Extract chunk data
          const data = new Uint8Array(chunk.byteLength);
          chunk.copyTo(data);

          muxer.addChunk({
            data,
            timestamp: chunk.timestamp,
            duration: chunk.duration ?? 0,
            isKeyFrame: chunk.type === 'key',
          });
        },
        error: (error: DOMException) => {
          reject(new Error(`Audio encoding failed: ${error.message}`));
        },
      });

      // Configure encoder
      encoder.configure({
        codec: 'opus',
        sampleRate: OUTPUT_SAMPLE_RATE,
        numberOfChannels: OUTPUT_CHANNELS,
        bitrate: OUTPUT_BITRATE,
      });

      // Resample if needed and encode
      const resampleRatio = OUTPUT_SAMPLE_RATE / pcmData.sampleRate;
      let timestamp = 0;

      for (const frame of pcmData.samples) {
        // Simple linear resampling if sample rates differ
        let samples: Float32Array;
        if (Math.abs(resampleRatio - 1) < 0.001) {
          samples = frame;
        } else {
          const newLength = Math.round(frame.length * resampleRatio);
          samples = new Float32Array(newLength);
          for (let i = 0; i < newLength; i++) {
            const srcIndex = i / resampleRatio;
            const srcIndexFloor = Math.floor(srcIndex);
            const srcIndexCeil = Math.min(srcIndexFloor + 1, frame.length - 1);
            const t = srcIndex - srcIndexFloor;
            samples[i] = frame[srcIndexFloor] * (1 - t) + frame[srcIndexCeil] * t;
          }
        }

        // Create AudioData for encoding
        const audioData = new AudioData({
          format: 'f32-planar',
          sampleRate: OUTPUT_SAMPLE_RATE,
          numberOfFrames: samples.length,
          numberOfChannels: OUTPUT_CHANNELS,
          timestamp,
          data: samples,
        });

        encoder.encode(audioData);
        audioData.close();

        // Update timestamp (in microseconds)
        timestamp += (samples.length / OUTPUT_SAMPLE_RATE) * 1_000_000;

        processedFrames++;
        onProgress(processedFrames / totalFrames);
      }

      // Flush encoder and finalize
      encoder.flush().then(() => {
        encoder.close();
        const blob = muxer.finalize();
        resolve(blob);
      }).catch(reject);
    });
  }

  /**
   * Generate output filename
   */
  private getOutputFileName(originalName: string): string {
    const baseName = originalName.replace(/\.[^/.]+$/, '') || 'audio';
    return `${baseName}.webm`;
  }
}
